(function () {
  'use strict';

  if (customElements.get('presale-carousel')) return;

  class PresaleCarousel extends HTMLElement {
    constructor() {
      super();
      this.rail = null;
      this.cards = [];
      this.previousButton = null;
      this.nextButton = null;
      this.progress = null;
      this.progressThumb = null;
      this.status = null;
      this.resizeObserver = null;
      this.frame = 0;
      this.connected = false;
      this.loopEnabled = false;
      this.loopItems = [];
      this.loopLogicalIndices = [];
      this.loopCloneCount = 0;
      this.loopCloneCycles = 0;
      this.loopSettleTimeout = 0;
      this.initialLoopPosition = false;
      this.loopPositioned = false;
      this.pendingResizeLogicalIndex = null;
      this.lastLogicalIndex = 0;
      this.navigationTarget = null;
      this.originalScrollSnap = null;
      this.pointerActive = false;
      this.resizeSignature = '';

      this.onScroll = () => {
        this.scheduleUpdate();
        this.scheduleLoopSettle();
      };
      this.onScrollEnd = () => this.settleLoop();
      this.onResize = () => {
        const signature = this.getResizeSignature();
        // ResizeObserver also notifies once after observe(), even when nothing
        // changed. That notification must not cancel a new click on reconnect.
        if (signature === this.resizeSignature) return;
        this.resizeSignature = signature;
        if (this.loopEnabled && (this.loopPositioned || this.navigationTarget)) {
          this.pendingResizeLogicalIndex = this.navigationTarget ? this.navigationTarget.index : this.lastLogicalIndex;
        }
        this.cancelNavigation();
        this.scheduleUpdate();
      };
      this.onPointerDown = () => {
        this.pointerActive = true;
        this.cancelNavigation();
      };
      this.onPointerUp = () => {
        if (!this.pointerActive) return;
        this.pointerActive = false;
        this.scheduleLoopSettle();
      };
      this.onWheel = () => this.cancelNavigation();
      this.onLoad = (event) => {
        if (event.target instanceof HTMLImageElement) this.scheduleUpdate();
      };
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onPrevious = () => this.moveBy(-1);
      this.onNext = () => this.moveBy(1);
      this.onBlockSelect = (event) => {
        const card = event.target.closest('[data-presale-card]');
        if (!card || !this.rail || !this.rail.contains(card)) return;
        const index = this.cards.indexOf(card);
        if (index < 0) return;
        const start = this.getCardStarts()[index];
        this.scrollTo(Math.min(this.getMaximumScroll(), Math.max(0, start - this.getRailPaddingLeft())), this.loopEnabled ? index : null);
      };
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.rail = this.querySelector('[data-presale-rail]');
      if (this.rail) this.rail.querySelectorAll('[data-presale-clone]').forEach((clone) => clone.remove());
      this.cards = this.rail ? Array.from(this.rail.querySelectorAll('[data-presale-card]')) : [];
      this.setupLoop();
      this.previousButton = this.querySelector('[data-presale-prev]');
      this.nextButton = this.querySelector('[data-presale-next]');
      this.progress = this.querySelector('[data-presale-progress]');
      this.progressThumb = this.progress && this.progress.querySelector('span');
      this.status = this.querySelector('[data-presale-status]');

      if (this.rail) {
        this.rail.addEventListener('scroll', this.onScroll, { passive: true });
        this.rail.addEventListener('scrollend', this.onScrollEnd);
        this.rail.addEventListener('keydown', this.onKeydown);
        this.rail.addEventListener('load', this.onLoad, true);
        this.rail.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.rail.addEventListener('wheel', this.onWheel, { passive: true });
      }
      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pointerup', this.onPointerUp, { passive: true });
      window.addEventListener('pointercancel', this.onPointerUp, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        if (this.rail) this.resizeObserver.observe(this.rail);
        this.cards.forEach((card) => this.resizeObserver.observe(card));
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.connected) this.scheduleUpdate();
        });
      }
      // Reconnection can happen in the same task as another button click.
      // Refresh bounds and the initial loop position before accepting input.
      this.update();
      this.resizeSignature = this.getResizeSignature();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      this.cancelNavigation();
      this.pointerActive = false;
      if (this.rail) {
        this.rail.removeEventListener('scroll', this.onScroll);
        this.rail.removeEventListener('scrollend', this.onScrollEnd);
        this.rail.removeEventListener('keydown', this.onKeydown);
        this.rail.removeEventListener('load', this.onLoad, true);
        this.rail.removeEventListener('pointerdown', this.onPointerDown);
        this.rail.removeEventListener('wheel', this.onWheel);
      }
      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = null;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (this.loopSettleTimeout) window.clearTimeout(this.loopSettleTimeout);
      this.loopSettleTimeout = 0;
      if (this.rail) this.rail.querySelectorAll('[data-presale-clone]').forEach((clone) => clone.remove());
      this.loopItems = [];
      this.loopLogicalIndices = [];
      this.loopCloneCount = 0;
      this.loopCloneCycles = 0;
      this.loopEnabled = false;
      this.initialLoopPosition = false;
      this.loopPositioned = false;
      this.pendingResizeLogicalIndex = null;
    }

    setupLoop() {
      this.loopEnabled = this.hasAttribute('data-presale-loop') && this.cards.length > 1;
      this.loopItems = this.cards.slice();
      this.loopLogicalIndices = this.cards.map((card, index) => index);
      this.loopCloneCount = 0;
      this.loopCloneCycles = 0;
      this.initialLoopPosition = false;
      this.loopPositioned = false;
      this.pendingResizeLogicalIndex = null;
      this.lastLogicalIndex = 0;
      if (!this.loopEnabled) return;

      this.initialLoopPosition = true;
      this.rebuildLoopClones(this.getRequiredLoopCycles());
    }

    getRequiredLoopCycles() {
      if (!this.rail || !this.cards.length) return 1;
      const style = window.getComputedStyle(this.rail);
      const gap = parseFloat(style.columnGap) || 0;
      const cycleWidth = this.cards.reduce((width, card) => width + card.getBoundingClientRect().width + gap, 0);
      const horizontalPadding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      if (!cycleWidth) return 1;
      return Math.max(1, Math.ceil((this.rail.clientWidth + horizontalPadding) / cycleWidth) + 1);
    }

    rebuildLoopClones(cycles) {
      this.rail.querySelectorAll('[data-presale-clone]').forEach((clone) => clone.remove());
      const repeatedCards = Array.from({ length: cycles }, () => this.cards).flat();
      const leadingClones = repeatedCards.map((card) => this.createLoopClone(card));
      const trailingClones = repeatedCards.map((card) => this.createLoopClone(card));
      const leadingFragment = document.createDocumentFragment();
      const trailingFragment = document.createDocumentFragment();
      leadingClones.forEach((clone) => leadingFragment.append(clone));
      trailingClones.forEach((clone) => trailingFragment.append(clone));
      this.rail.insertBefore(leadingFragment, this.cards[0]);
      this.rail.append(trailingFragment);
      this.loopCloneCycles = cycles;
      this.loopCloneCount = this.cards.length * cycles;
      this.loopItems = [...leadingClones, ...this.cards, ...trailingClones];
      this.loopLogicalIndices = [
        ...repeatedCards.map((card, index) => index % this.cards.length),
        ...this.cards.map((card, index) => index),
        ...repeatedCards.map((card, index) => index % this.cards.length),
      ];
    }

    createLoopClone(card) {
      const clone = card.cloneNode(true);
      [clone, ...clone.querySelectorAll('*')].forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
          if (attribute.name === 'id' || attribute.name === 'data-presale-card' ||
              attribute.name === 'data-presale-block' || attribute.name === 'data-block-id' ||
              attribute.name === 'data-shopify-block-id' || attribute.name.startsWith('data-shopify')) {
            node.removeAttribute(attribute.name);
          }
        });
      });
      clone.setAttribute('data-presale-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      return clone;
    }

    getRailPaddingLeft() {
      return this.rail ? parseFloat(window.getComputedStyle(this.rail).paddingLeft) || 0 : 0;
    }

    getResizeSignature() {
      if (!this.rail) return '';
      const style = window.getComputedStyle(this.rail);
      return [style.paddingLeft, style.paddingRight, style.columnGap, ...[this.rail, ...this.cards].flatMap((element) => {
        const rect = element.getBoundingClientRect();
        return [rect.width, rect.height];
      })].join(':');
    }

    getStartsFor(items) {
      const railLeft = this.rail.getBoundingClientRect().left + this.rail.clientLeft;
      const scrollLeft = this.rail.scrollLeft;
      return items.map((card) => card.getBoundingClientRect().left - railLeft + scrollLeft);
    }

    getNearestItemIndex(starts) {
      const scrollLeft = Math.max(0, this.rail.scrollLeft);
      const padding = this.loopEnabled ? this.getRailPaddingLeft() : 0;
      return starts.reduce((nearest, start, index) =>
        Math.abs(start - padding - scrollLeft) < Math.abs(starts[nearest] - padding - scrollLeft) ? index : nearest, 0);
    }

    getActiveLogicalIndex() {
      if (!this.loopEnabled || !this.loopItems.length) return 0;
      const index = this.getNearestItemIndex(this.getStartsFor(this.loopItems));
      return this.loopLogicalIndices[index] ?? this.lastLogicalIndex;
    }

    scheduleUpdate() {
      if (!this.connected || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    scheduleLoopSettle() {
      if (!this.loopEnabled && !this.navigationTarget) return;
      if (this.loopSettleTimeout) window.clearTimeout(this.loopSettleTimeout);
      this.loopSettleTimeout = window.setTimeout(() => {
        this.loopSettleTimeout = 0;
        this.settleLoop();
      }, 160);
    }

    settleLoop() {
      if (!this.connected || !this.rail || this.pointerActive) return;
      if (this.loopSettleTimeout) window.clearTimeout(this.loopSettleTimeout);
      this.loopSettleTimeout = 0;
      // An old scrollend (or idle timer) can arrive after another click retargets
      // the native animation. Only finish the current request at its destination.
      if (this.navigationTarget && Math.abs(this.rail.scrollLeft - this.navigationTarget.left) > 1) return;
      this.navigationTarget = null;
      if (!this.loopEnabled || !this.loopItems.length) {
        this.restoreScrollSnap();
        this.scheduleUpdate();
        return;
      }
      const starts = this.getStartsFor(this.loopItems);
      const nearest = this.getNearestItemIndex(starts);
      const firstRealIndex = this.loopCloneCount;
      const lastRealIndex = firstRealIndex + this.cards.length - 1;
      if (nearest < firstRealIndex || nearest > lastRealIndex) {
        const logicalIndex = this.loopLogicalIndices[nearest] ?? this.lastLogicalIndex;
        const realIndex = firstRealIndex + logicalIndex;
        const left = starts[realIndex] - this.getRailPaddingLeft();
        this.rail.scrollTo({ left, behavior: 'instant' });
        this.lastLogicalIndex = logicalIndex;
      }
      this.restoreScrollSnap();
      this.scheduleUpdate();
    }

    suspendScrollSnap() {
      if (this.originalScrollSnap !== null) return;
      this.originalScrollSnap = {
        value: this.rail.style.getPropertyValue('scroll-snap-type'),
        priority: this.rail.style.getPropertyPriority('scroll-snap-type'),
      };
      this.rail.style.setProperty('scroll-snap-type', 'none');
    }

    restoreScrollSnap() {
      if (this.originalScrollSnap === null || !this.rail) return;
      const { value, priority } = this.originalScrollSnap;
      if (value) this.rail.style.setProperty('scroll-snap-type', value, priority);
      else this.rail.style.removeProperty('scroll-snap-type');
      this.originalScrollSnap = null;
    }

    cancelNavigation() {
      if (this.navigationTarget && this.rail) {
        this.navigationTarget = null;
        this.rail.scrollTo({ left: this.rail.scrollLeft, behavior: 'instant' });
        this.scheduleUpdate();
      }
      this.restoreScrollSnap();
    }

    getMaximumScroll() {
      return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    }

    getCardStarts() {
      return this.getStartsFor(this.cards);
    }

    getFiniteTargets() {
      const maximum = this.getMaximumScroll();
      const padding = this.getRailPaddingLeft();
      return [0, ...this.getCardStarts().map((start) => Math.min(maximum, Math.max(0, start - padding)))].filter((left, index, targets) => index === 0 || Math.abs(left - targets[index - 1]) > 1);
    }

    update() {
      if (!this.rail) return;

      // Complete layout reads before updating buttons, progress, or status.
      const maximum = this.getMaximumScroll();
      const scrollLeft = Math.max(0, this.rail.scrollLeft);
      const loopStarts = this.loopEnabled ? this.getStartsFor(this.loopItems) : [];
      const railRect = this.rail.getBoundingClientRect();
      const railLeft = railRect.left + this.rail.clientLeft;
      const visibleLeft = scrollLeft;
      const visibleRight = scrollLeft + this.rail.clientWidth;
      const visibleIndices = [];
      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardLeft = rect.left - railLeft + scrollLeft;
        const cardRight = cardLeft + card.offsetWidth;
        if (cardRight > visibleLeft + 1 && cardLeft < visibleRight - 1) visibleIndices.push(index);
      });
      const visibleCount = visibleIndices.length;
      const firstVisible = visibleCount ? visibleIndices[0] + 1 : 0;
      const lastVisible = visibleCount ? visibleIndices[visibleCount - 1] + 1 : 0;
      const activeItemIndex = this.loopEnabled ? this.getNearestItemIndex(loopStarts) : -1;
      const activeLogicalIndex = this.loopEnabled
        ? (this.loopLogicalIndices[activeItemIndex] ?? this.lastLogicalIndex)
        : 0;
      if (this.loopEnabled) this.lastLogicalIndex = activeLogicalIndex;
      if (this.loopEnabled && !this.initialLoopPosition && this.getRequiredLoopCycles() !== this.loopCloneCycles) {
        if (this.pendingResizeLogicalIndex === null) this.pendingResizeLogicalIndex = this.navigationTarget ? this.navigationTarget.index : activeLogicalIndex;
        this.cancelNavigation();
        this.rebuildLoopClones(this.getRequiredLoopCycles());
        this.scheduleUpdate();
        return;
      }
      const thumbFraction = this.loopEnabled
        ? 1 / this.cards.length
        : (this.cards.length ? Math.min(1, visibleCount / this.cards.length) : 1);
      const scrollFraction = maximum ? Math.min(1, Math.max(0, scrollLeft / maximum)) : 0;
      const thumbOffset = this.loopEnabled
        ? (this.cards.length > 1 ? activeLogicalIndex / (this.cards.length - 1) * (1 / thumbFraction - 1) * 100 : 0)
        : (thumbFraction < 1 ? scrollFraction * (1 / thumbFraction - 1) * 100 : 0);
      const canScroll = this.loopEnabled ? this.cards.length > 1 : maximum > 1 && this.cards.length > 1;
      const controlLeft = !this.loopEnabled && this.navigationTarget ? this.navigationTarget.left : scrollLeft;
      const atStart = controlLeft <= 1;
      const atEnd = controlLeft >= maximum - 1;

      if (this.previousButton) this.previousButton.disabled = !canScroll || (!this.loopEnabled && atStart);
      if (this.nextButton) this.nextButton.disabled = !canScroll || (!this.loopEnabled && atEnd);
      if (this.progressThumb) {
        this.progressThumb.style.width = `${thumbFraction * 100}%`;
        this.progressThumb.style.transform = `translateX(${thumbOffset}%)`;
      }
      if (this.status) {
        this.status.textContent = this.loopEnabled
          ? `Item ${activeLogicalIndex + 1} of ${this.cards.length}`
          : (this.cards.length && visibleCount
            ? `Items ${firstVisible}–${lastVisible} of ${this.cards.length}`
            : `Items 0 of ${this.cards.length}`);
      }
      if (this.loopEnabled && (this.initialLoopPosition || this.pendingResizeLogicalIndex !== null)) {
        const logicalIndex = this.initialLoopPosition ? 0 : this.pendingResizeLogicalIndex;
        const targetIndex = this.loopCloneCount + Math.max(0, Math.min(this.cards.length - 1, logicalIndex));
        const targetLeft = loopStarts[targetIndex] - this.getRailPaddingLeft();
        this.initialLoopPosition = false;
        this.pendingResizeLogicalIndex = null;
        this.lastLogicalIndex = Math.max(0, Math.min(this.cards.length - 1, logicalIndex));
        this.rail.scrollTo({ left: targetLeft, behavior: 'instant' });
        this.scheduleUpdate();
      } else if (this.loopEnabled && !this.loopPositioned) {
        this.loopPositioned = true;
      }
    }

    moveBy(direction) {
      if (!this.rail || this.cards.length < 2) return;
      const maximum = this.getMaximumScroll();
      if (maximum <= 1 && !this.loopEnabled) return;
      let current = Math.max(0, this.rail.scrollLeft);
      if (this.loopEnabled) {
        const starts = this.getStartsFor(this.loopItems);
        let nearestIndex = this.getNearestItemIndex(starts);
        const activeLogicalIndex = this.loopLogicalIndices[nearestIndex] ?? 0;
        const requestedIndex = this.navigationTarget ? this.navigationTarget.index : activeLogicalIndex;
        const targetLogicalIndex = (requestedIndex + direction + this.cards.length) % this.cards.length;
        let targetIndex = this.navigationTarget ? this.navigationTarget.itemIndex : nearestIndex;
        const padding = this.getRailPaddingLeft();
        const firstRealIndex = this.loopCloneCount;
        const lastRealIndex = firstRealIndex + this.cards.length - 1;
        this.suspendScrollSnap();
        if (nearestIndex < firstRealIndex || nearestIndex > lastRealIndex) {
          // Rebase an in-flight clone at the exact same visual offset. Keeping
          // snap suspended avoids rounding a partial card during this jump.
          const offset = current - (starts[nearestIndex] - padding);
          const shift = firstRealIndex + activeLogicalIndex - nearestIndex;
          nearestIndex = firstRealIndex + activeLogicalIndex;
          targetIndex += shift;
          current = starts[nearestIndex] - padding + offset;
          this.rail.scrollTo({ left: current, behavior: 'instant' });
        }
        targetIndex += direction;
        // Keep rapid bursts within one equivalent cycle of the visible cards.
        // Opposite clicks cancel pending steps instead of taking a full lap in
        // the new direction, while every click still changes the logical target.
        while (targetIndex - nearestIndex > this.cards.length) targetIndex -= this.cards.length;
        while (targetIndex - nearestIndex < -this.cards.length) targetIndex += this.cards.length;
        this.scrollTo(starts[targetIndex] - padding, targetLogicalIndex, targetIndex);
        return;
      }
      const targets = this.getFiniteTargets();
      const nearestIndex = this.navigationTarget ? this.navigationTarget.index : targets.reduce((nearest, left, index) =>
        Math.abs(left - current) < Math.abs(targets[nearest] - current) ? index : nearest, 0);
      const targetIndex = Math.min(targets.length - 1, Math.max(0, nearestIndex + direction));
      this.scrollTo(targets[targetIndex], targetIndex);
    }

    scrollTo(left, index = null, itemIndex = null) {
      left = Math.min(this.getMaximumScroll(), Math.max(0, left));
      if (index === null || (this.loopEnabled && itemIndex === null)) {
        const targets = this.loopEnabled ? this.getStartsFor(this.loopItems).map((start) => start - this.getRailPaddingLeft()) : this.getFiniteTargets();
        const nearest = targets.reduce((result, target, item) => Math.abs(target - left) < Math.abs(targets[result] - left) ? item : result, 0);
        if (index === null) index = this.loopEnabled ? this.loopLogicalIndices[nearest] : nearest;
        if (this.loopEnabled) itemIndex = nearest;
      }
      this.suspendScrollSnap();
      this.navigationTarget = { index, itemIndex, left };
      if (!this.loopEnabled) {
        if (this.previousButton) this.previousButton.disabled = left <= 1;
        if (this.nextButton) this.nextButton.disabled = left >= this.getMaximumScroll() - 1;
      }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollTo({ left, behavior: reducedMotion ? 'instant' : 'smooth' });
      this.scheduleLoopSettle();
    }

    handleKeydown(event) {
      if (event.target !== this.rail || !this.rail) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const maximum = this.getMaximumScroll();
      if (maximum <= 1 && !this.loopEnabled) return;
      if (event.key === 'ArrowRight') this.moveBy(1);
      else if (event.key === 'ArrowLeft') this.moveBy(-1);
      else if (event.key === 'Home') {
        if (this.loopEnabled) this.scrollTo(this.getStartsFor(this.loopItems)[this.loopCloneCount] - this.getRailPaddingLeft(), 0);
        else this.scrollTo(0);
      } else if (event.key === 'End') {
        if (this.loopEnabled) this.scrollTo(this.getStartsFor(this.loopItems)[this.loopCloneCount + this.cards.length - 1] - this.getRailPaddingLeft(), this.cards.length - 1);
        else this.scrollTo(maximum);
      }
      else return;
      event.preventDefault();
    }
  }

  customElements.define('presale-carousel', PresaleCarousel);
})();
